import React, { useState, useRef, useEffect } from 'react';
import { Checkbox, CTA, Richtext } from '@volkswagen-onehub/components-core';
import {
  StyledCheckBoxContainer,
  StyledTosWrapper,
  StyledSubmitButtonContainer,
  StyledLegalContainer,
} from '../styles/tos-styles';
import { onVWBasicFormStepButtonClickEvent, onVWBasicFormStepLoadEvent } from '../../../shared/tagging/tagging-helpers';
import { useFormErrorHandling } from '../../../shared/hooks/use-form-error-handling';
import { useFeatureServices } from '../../../shared/hooks/use-feature-services';
import { useMyVwContext } from '../../../shared/hooks/use-my-vw-context';
import { useTrackingManager } from '../../../shared/hooks/use-tracking-manager';
import ppEnCA from '../../../legal/pp-en-ca';
import ppFrCA from '../../../legal/pp-fr-ca';
import { useFormik } from 'formik';

interface PrivacyPolicyProps {
  onComplete: () => void;
  initialScrollComplete?: boolean;
}

export const PrivacyPolicy = ({ onComplete, initialScrollComplete = false }: PrivacyPolicyProps) => {
  const { authService, translationHandler } = useMyVwContext();
  const { 'locale-service': localeService } = useFeatureServices();
  const isFrench = localeService?.language === 'fr';
  const localeCode = `${localeService.language}-${localeService.countryCode}`;

  const { setIsSubmitting, handleSubmitError } = useFormErrorHandling({ authService });
  const trackingManager = useTrackingManager();

  const [hasScrolledToEnd, setHasScrolledToEnd] = useState<boolean>(initialScrollComplete);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const checkScrollEnd = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const { scrollTop, clientHeight, scrollHeight } = container;
    const isEnd = scrollTop + clientHeight >= scrollHeight - 1;
    if (isEnd && !hasScrolledToEnd) {
      setHasScrolledToEnd(true);
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.addEventListener('scroll', checkScrollEnd);
    checkScrollEnd();
    return () => container.removeEventListener('scroll', checkScrollEnd);
  }, []);

  useEffect(() => {
    onVWBasicFormStepLoadEvent(trackingManager, {});
  }, []);

  const handleFormNextTracking = () => {
    onVWBasicFormStepButtonClickEvent(trackingManager, {
      Type: 'Button',
      Name: translationHandler('registration.next.cta.label'),
    });
  };

  const formik = useFormik({
    initialValues: {
      acceptedPp: false,
    },
    onSubmit: async (values) => {
      if (!hasScrolledToEnd || !values.acceptedPp) return;

      setIsSubmitting(true);
      try {
        await authService.saveUserMyVWTosResponse(localeCode, "PRIVACY_POLICY", "Accept");
        handleFormNextTracking();
        onComplete();
      } catch (error) {
        handleSubmitError(error);
      } finally {
        setIsSubmitting(false);
      }
    },
    validateOnBlur: true,
    validateOnChange: true,
  });

  const handleCheckboxChange = (e: any) => {
    if (!hasScrolledToEnd) return;
    const isChecked = e.target?.checked ?? false;
    formik.setFieldValue('acceptedPp', isChecked);
  };

  const PPCheckBox = () => (
    <StyledCheckBoxContainer>
      <Checkbox
        aria-required="true"
        aria-label={translationHandler('registration.privacypolicy.checkbox')}
        label={<>{translationHandler('registration.privacypolicy.checkbox')}</>}
        required
        onChange={handleCheckboxChange}
        checked={formik.values.acceptedPp}
        disabled={!hasScrolledToEnd}
        value={String(formik.values.acceptedPp)}
        name="acceptedPp"
      />
    </StyledCheckBoxContainer>
  );

  return (
    <StyledTosWrapper>
      <StyledLegalContainer ref={scrollContainerRef}>
        <Richtext
          markdown={isFrench ? ppFrCA : ppEnCA}
          dangerouslyParseRawHTML
        />
      </StyledLegalContainer>
      <form onSubmit={formik.handleSubmit}>
        <PPCheckBox />
        <StyledSubmitButtonContainer>
          <CTA
            aria-label="Submit button"
            emphasis="primary"
            stretchContent
            tag="button"
            type="submit"
            disabled={!formik.values.acceptedPp}
          >
            {translationHandler('registration.next.cta.label')}
          </CTA>
        </StyledSubmitButtonContainer>
      </form>
    </StyledTosWrapper>
  );
};
